import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { sortByKey } from '@semios/app-platform-common'
import { awcHeatmap } from 'App/Map/PanelDetails/StackemCharts/_utils/by-domain/soil/awcHeatmap'
import moment from 'moment-timezone'
import type { TIrrigationZoneEmitterId, TProperty } from 'stores/fieldAssetStore/helperTypes'
import { buildAwcLineAndWaterEvents } from './buildAwcLineAndWaterEvents'

export const buildSoilChartData = ({
  lngLats,
  selectedProperty,
  soilData,
  precipitationStationLngLat,
  timezone,
}: {
  lngLats: string[]
  selectedProperty: TProperty
  soilData: routes.Values.Response
  precipitationStationLngLat: string | null
  timezone: string
}) => {
  const chartContent = lngLats
    .flatMap((lngLat) => {
      const soilPointOnProperty = Object.values(selectedProperty.points ?? {}).find((point) => {
        return point?.lngLat === lngLat
      })

      const zoneEmittersThatIIntersectWith =
        soilPointOnProperty?.configuration.soilProbeIntersectingZoneIdsWithEmitters?.[0]

      if (!zoneEmittersThatIIntersectWith) {
        return []
      }

      const zoneName =
        zoneEmittersThatIIntersectWith &&
        selectedProperty.irrigationZoneEmitters?.[zoneEmittersThatIIntersectWith]?.name

      const soilStationFavoriteDepths = soilPointOnProperty?.configuration?.soilDefaultProbeDepths ?? []

      if (!zoneName) {
        return []
      }

      const irrigationActivity =
        soilData?.points?.[lngLat]?.values?.soilMoisture?.[0]?.metadata?.irrigationActivityForZones

      const intervalsForChart = Object.entries(irrigationActivity ?? {})
        .filter(([, emitterSeries]) => emitterSeries.length > 0)
        .map(([irrigationZoneEmitterId, emitterSeries]) => {
          let onDurationMinutes = 0

          emitterSeries.forEach((s) => {
            const startTime = moment.tz(s.startTime, timezone)
            const endTime = moment.tz(s.endTime, timezone)
            const minutes = endTime.diff(startTime, 'minutes')

            onDurationMinutes += minutes
          })

          return {
            irrigationZoneEmitterId: irrigationZoneEmitterId as TIrrigationZoneEmitterId,
            emitterType: emitterSeries[0].emitterType,
            zoneId: emitterSeries[0].zoneId,
            onDurationMinutes,
          }
        })

      return {
        title: `${zoneName} (${soilPointOnProperty?.name})`,
        intervalsForChart,
        awcChart: buildAwcLineAndWaterEvents({
          data: soilData,
          selectedProperty,
          soilStationLngLat: lngLat,
          precipitationStationLngLat,
          soilStationFavoriteDepths,
          compareSeasonsData: {},
        }),
        awcHeatmap: awcHeatmap({
          data: soilData,
          soilStationLngLat: lngLat,
        }),
        soilStationFavoriteDepths,
      }
    })
    .sort(sortByKey('title'))

  return chartContent
}
